import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock, Facebook, Instagram } from 'lucide-react';

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer data-testid="main-footer" className="bg-primary text-primary-foreground">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
          {/* Brand */}
          <div className="lg:col-span-1">
            <Link to="/" className="inline-block mb-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-primary-foreground flex items-center justify-center">
                  <span className="text-primary font-serif text-xl font-semibold">A</span>
                </div>
                <span className="font-serif text-2xl font-semibold">Alma Fleur</span>
              </div>
            </Link>
            <p className="text-primary-foreground/70 leading-relaxed mb-6">
              Votre fleuriste artisan à Sion depuis plus de 20 ans. Créations uniques et livraison locale.
            </p>
            <div className="flex gap-4">
              <a
                href="https://facebook.com"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-facebook"
                className="w-10 h-10 rounded-full bg-primary-foreground/10 flex items-center justify-center hover:bg-primary-foreground/20 transition-colors"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a
                href="https://instagram.com"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-instagram"
                className="w-10 h-10 rounded-full bg-primary-foreground/10 flex items-center justify-center hover:bg-primary-foreground/20 transition-colors"
              >
                <Instagram className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-serif text-lg font-semibold mb-6">Navigation</h3>
            <nav className="space-y-3">
              {[
                { name: 'Accueil', path: '/' },
                { name: 'À propos', path: '/a-propos' },
                { name: 'Services', path: '/services' },
                { name: 'Galerie', path: '/galerie' },
                { name: 'Contact', path: '/contact' },
              ].map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`footer-link-${link.name.toLowerCase().replace(/\s/g, '-')}`}
                  className="block text-primary-foreground/70 hover:text-primary-foreground transition-colors"
                >
                  {link.name}
                </Link>
              ))}
            </nav>
          </div>

          {/* Services */}
          <div>
            <h3 className="font-serif text-lg font-semibold mb-6">Nos Services</h3>
            <nav className="space-y-3">
              {[
                'Bouquets & Compositions',
                'Mariages',
                'Événements',
                'Deuil & Hommages',
                'Livraison',
              ].map((service) => (
                <span
                  key={service}
                  className="block text-primary-foreground/70"
                >
                  {service}
                </span>
              ))}
            </nav>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="font-serif text-lg font-semibold mb-6">Contact</h3>
            <div className="space-y-4">
              <a
                href="https://www.google.com/maps/dir/?api=1&destination=Av.+Ritz+33,+1950+Sion,+Suisse"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-address"
                className="flex items-start gap-3 text-primary-foreground/70 hover:text-primary-foreground transition-colors"
              >
                <MapPin className="w-5 h-5 mt-0.5 flex-shrink-0" />
                <span>Av. Ritz 33<br />1950 Sion, Suisse</span>
              </a>
              <a
                href="tel:+41273232316"
                data-testid="footer-phone"
                className="flex items-center gap-3 text-primary-foreground/70 hover:text-primary-foreground transition-colors"
              >
                <Phone className="w-5 h-5 flex-shrink-0" />
                <span>+41 27 323 23 16</span>
              </a>
              <a
                href="mailto:info@alma-fleurs-sion.ch"
                data-testid="footer-email"
                className="flex items-center gap-3 text-primary-foreground/70 hover:text-primary-foreground transition-colors"
              >
                <Mail className="w-5 h-5 flex-shrink-0" />
                <span>info@alma-fleurs-sion.ch</span>
              </a>
              <div className="flex items-start gap-3 text-primary-foreground/70">
                <Clock className="w-5 h-5 mt-0.5 flex-shrink-0" />
                <div>
                  <span className="block">Lun - Ven: 09:00 - 18:30</span>
                  <span className="block">Sam: 09:00 - 17:00</span>
                  <span className="block text-accent">Dim: Fermé</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-primary-foreground/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-primary-foreground/50">
              © {currentYear} Alma Fleur SA. Tous droits réservés.
            </p>
            <div className="flex gap-6 text-sm text-primary-foreground/50">
              <span>Sion, Valais, Suisse</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
